import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { catalogueKeys, fetchOccasions } from '@/api/catalogue';
import { BrandArtwork } from '@/components/brand/BrandArtwork';
import type { ColourwayId } from '@/components/brand/BrandArtwork';
import { Skeleton, SkeletonRegion } from '@/components/ui/Skeleton';

/**
 * "Shop by occasion" — BUILD-PLAN §11, §17.
 *
 * Driven entirely by the occasions API, so a new occasion created in admin
 * appears here without a code change, and an out-of-season one disappears.
 *
 * Renders nothing at all when there are no occasions or the request fails:
 * §86 forbids showing a section that pretends to have content, and a homepage
 * missing one strip reads better than one showing an error card.
 *
 * ## Why these are cards and not chips
 *
 * This was a row of outlined pills — the control a form uses to pick a tag.
 * Nine of them wrapped across the page looked like filter UI that had escaped
 * onto the homepage, and it gave the eye nothing: no image, no depth, no
 * reason to choose one over another. An occasion is a mood, and a mood has to
 * be shown rather than labelled.
 *
 * Each is now a close-cropped card with its own artwork, the name set over a
 * gradient that resolves out of the image, and a lift on hover. That is the
 * boutique treatment the brief asked for: density, layering and real depth
 * rather than flat panels.
 */

/**
 * Eight, not nine.
 *
 * The grid runs two-up then four-up, both of which divide eight exactly. The
 * API currently returns nine occasions, and the ninth would sit alone on a
 * final row — a widow that reads as a rendering fault rather than as a
 * deliberate row. The heading carries a "View all" link to the rest.
 */
const MAX_TILES = 8;

/**
 * The shelf is DEALT, not hashed.
 *
 * BrandArtwork normally derives its colourway from the seed, which is right
 * for a product — the same product should always look the same. It is wrong
 * for a set viewed all at once: of the six colourways four are dark, so a
 * hash lands roughly two thirds of the tiles on near-black and clumps them
 * unpredictably. The shelf came out as six black tiles and two pale ones in
 * no particular order, which looks like an accident rather than a palette.
 *
 * Alternating dark and light by POSITION guarantees rhythm across the row at
 * every column count — 2, or 4 — because the sequence alternates on every
 * index. The two light colourways repeat; with four darks and two lights
 * there is no way to alternate eight tiles without repeating, and repeating
 * the lights reads better than clustering the darks.
 */
const TILE_COLOURWAYS: ColourwayId[] = [
  'onyx',
  'champagne',
  'burgundy',
  'ivory',
  'forest',
  'champagne',
  'noir',
  'ivory',
];

/** Cycled alongside the colourways so neighbouring tiles differ in shape too. */
const TILE_MOTIFS = ['parcel', 'ribbon', 'monogram', 'arch'] as const;

export function OccasionStrip() {
  const { data: occasions = [], isPending, isError } = useQuery({
    queryKey: catalogueKeys.occasions,
    queryFn: fetchOccasions,
    staleTime: 10 * 60 * 1000,
  });

  if (isError) {
    return null;
  }

  if (isPending) {
    return (
      <SkeletonRegion label="Loading occasions">
        <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-4 sm:gap-x-5">
          {Array.from({ length: MAX_TILES }, (_, index) => (
            <Skeleton key={index} className="aspect-3/2 w-full sm:aspect-4/3" />
          ))}
        </div>
      </SkeletonRegion>
    );
  }

  if (occasions.length === 0) {
    return null;
  }

  return (
    <ul className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-4 sm:gap-x-5">
      {occasions.slice(0, MAX_TILES).map((occasion, position) => (
        <li key={occasion.slug}>
          <Link
            to={`/shop?occasions=${occasion.slug}`}
            className="group block focus-visible:outline-none"
          >
            {/*
              4:3 — a close crop, deliberately wider than it is tall. A tall
              portrait tile would make eight of these the tallest thing on the
              page, which is the "images are too big" fault in another guise.
            */}
            <div className="relative aspect-3/2 overflow-hidden bg-surface-muted shadow-card transition-shadow duration-300 group-hover:shadow-raised sm:aspect-4/3">
              <BrandArtwork
                seed={occasion.name}
                colourway={TILE_COLOURWAYS[position % TILE_COLOURWAYS.length]!}
                motif={TILE_MOTIFS[position % TILE_MOTIFS.length]!}
                ratio="wide"
                className="h-full w-full transition-transform duration-500 ease-out group-hover:scale-[1.04]"
              />

              {/*
                The label sits ON the image, over a gradient rising out of it
                rather than a flat scrim — a hard translucent bar across the
                bottom is the giveaway of a template.
              */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-linear-to-t from-ink/92 via-ink/45 to-transparent"
              />

              {/* Inset keyline, echoing the boxed presentation of the
                  products themselves. */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-2.5 border border-ivory/20 transition-colors duration-300 group-hover:border-ivory/35"
              />

              <span className="absolute inset-x-0 bottom-0 flex items-center gap-2 p-3.5 sm:p-4">
                <span className="h-px w-4 shrink-0 bg-accent/70 transition-all duration-300 group-hover:w-6" />
                <span className="font-display text-sm leading-tight text-ivory sm:text-base">
                  {occasion.name}
                </span>
              </span>

              {/* The focus ring lives here rather than on the anchor so it
                  traces the card, not an invisible inline box. */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 ring-accent transition-shadow group-focus-visible:ring-2"
              />
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}
